import React, { useEffect } from "react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import {
  SuggestionListWidgetProps,
  SuggestionListWidgetState,
  SuggestionListPreset,
} from "./types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import { FONT_WEIGHT_OPTIONS_KEYWORDS } from "../../../../utils/fontWeightOptions";
import { themeColor } from "../../../../types/color";

/**
 * Load a Google Font stylesheet into document.head.
 * This is necessary because fonts loaded inside Shadow DOM don't register
 * @font-face declarations globally, causing the font to not render.
 */
const loadGoogleFontInHead = (fontUrl: string): void => {
  // Check if already loaded
  const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
  if (existingLink) return;

  // Inject into document.head (works with Shadow DOM)
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = fontUrl;
  document.head.appendChild(link);
};

const defaultState: SuggestionListWidgetState = {
  // Preset
  preset: "plain",

  // Layout direction
  layout: "vertical",

  // Container
  containerGap: 8,
  containerBgColor: themeColor("transparent"),
  containerPaddingX: 0,
  containerPaddingY: 0,
  containerBorderRadius: 0,

  // Icon
  showIcon: false,

  // Suggestion pills
  pillPaddingX: 16,
  pillPaddingY: 12,
  pillBorderRadius: 8,
  pillBorderColor: themeColor("borderColor"),
  pillBorderWidth: 1,
  pillBgColor: themeColor("secondary"),
  pillBgColorHover: themeColor("background"),
  pillTextColor: themeColor("textPrimary"),
  pillShadowEnabled: false,
  pillShadow: "0 4px 16px 0 rgba(0, 0, 0, 0.05)",
  pillFontSize: 14,
  pillFontWeight: "medium",
  pillFontFamily: "inherit",
};

/**
 * Preset definitions derived from Figma mockups.
 *
 * Matrix: 2 icon variants (none / arrow) x 3 background styles + 3 extras
 *
 * Base 6:
 *   plain          – no icon, transparent bg, thin border
 *   plain-icon     – arrow icon, transparent bg, thin border
 *   highlighted    – no icon, highlighted container bg, no border
 *   highlighted-icon – arrow icon, highlighted container bg, no border
 *   container-bg   – no icon, container bg, thin border
 *   container-bg-icon – arrow icon, container bg, thin border
 *
 * Extras:
 *   bordered       – no icon, transparent bg, stronger border
 *   bordered-icon  – arrow icon, transparent bg, stronger border
 *   bordered-highlighted – arrow icon, container bg, stronger border
 */
const presetDefinitions: Record<
  Exclude<SuggestionListPreset, "custom">,
  Partial<SuggestionListWidgetState>
> = {
  // --- Base: transparent bg, thin light border ---
  plain: {
    showIcon: false,
    containerGap: 8,
    containerBgColor: themeColor("transparent"),
    containerPaddingX: 0,
    containerPaddingY: 0,
    containerBorderRadius: 0,
    pillPaddingX: 16,
    pillPaddingY: 12,
    pillBorderRadius: 8,
    pillBorderColor: themeColor("borderColor"),
    pillBorderWidth: 1,
    pillBgColor: themeColor("secondary"),
    pillBgColorHover: themeColor("background"),
    pillTextColor: themeColor("textPrimary"),
    pillFontSize: 14,
    pillFontWeight: "medium",
  },
  "plain-icon": {
    showIcon: true,
    containerGap: 8,
    containerBgColor: themeColor("transparent"),
    containerPaddingX: 0,
    containerPaddingY: 0,
    containerBorderRadius: 0,
    pillPaddingX: 16,
    pillPaddingY: 12,
    pillBorderRadius: 8,
    pillBorderColor: themeColor("borderColor"),
    pillBorderWidth: 1,
    pillBgColor: themeColor("secondary"),
    pillBgColorHover: themeColor("background"),
    pillTextColor: themeColor("textPrimary"),
    pillFontSize: 14,
    pillFontWeight: "medium",
  },

  // --- Highlighted: container bg, no pill border ---
  highlighted: {
    showIcon: false,
    containerGap: 8,
    containerBgColor: themeColor("transparent"),
    containerPaddingX: 12,
    containerPaddingY: 12,
    containerBorderRadius: 12,
    pillPaddingX: 16,
    pillPaddingY: 12,
    pillBorderRadius: 8,
    pillBorderColor: themeColor("transparent"),
    pillBorderWidth: 0,
    pillBgColor: themeColor("secondary"),
    pillBgColorHover: themeColor("background"),
    pillTextColor: themeColor("textPrimary"),
    pillFontSize: 14,
    pillFontWeight: "medium",
  },
  "highlighted-icon": {
    showIcon: true,
    containerGap: 8,
    containerBgColor: themeColor("transparent"),
    containerPaddingX: 12,
    containerPaddingY: 12,
    containerBorderRadius: 12,
    pillPaddingX: 16,
    pillPaddingY: 12,
    pillBorderRadius: 8,
    pillBorderColor: themeColor("transparent"),
    pillBorderWidth: 0,
    pillBgColor: themeColor("secondary"),
    pillBgColorHover: themeColor("background"),
    pillTextColor: themeColor("textPrimary"),
    pillFontSize: 14,
    pillFontWeight: "medium",
  },

  // --- Container bg with thin border on pills ---
  "container-bg": {
    showIcon: false,
    containerGap: 8,
    containerBgColor: themeColor("transparent"),
    containerPaddingX: 12,
    containerPaddingY: 12,
    containerBorderRadius: 12,
    pillPaddingX: 16,
    pillPaddingY: 12,
    pillBorderRadius: 8,
    pillBorderColor: themeColor("borderColor"),
    pillBorderWidth: 1,
    pillBgColor: themeColor("secondary"),
    pillBgColorHover: themeColor("background"),
    pillTextColor: themeColor("textPrimary"),
    pillFontSize: 14,
    pillFontWeight: "medium",
  },
  "container-bg-icon": {
    showIcon: true,
    containerGap: 8,
    containerBgColor: themeColor("transparent"),
    containerPaddingX: 12,
    containerPaddingY: 12,
    containerBorderRadius: 12,
    pillPaddingX: 16,
    pillPaddingY: 12,
    pillBorderRadius: 8,
    pillBorderColor: themeColor("borderColor"),
    pillBorderWidth: 1,
    pillBgColor: themeColor("secondary"),
    pillBgColorHover: themeColor("background"),
    pillTextColor: themeColor("textPrimary"),
    pillFontSize: 14,
    pillFontWeight: "medium",
  },

  // --- Extras: stronger border treatment ---
  bordered: {
    showIcon: false,
    containerGap: 6,
    containerBgColor: themeColor("transparent"),
    containerPaddingX: 0,
    containerPaddingY: 0,
    containerBorderRadius: 0,
    pillPaddingX: 16,
    pillPaddingY: 14,
    pillBorderRadius: 10,
    pillBorderColor: themeColor("borderColor"),
    pillBorderWidth: 1.5,
    pillBgColor: themeColor("secondary"),
    pillBgColorHover: themeColor("background"),
    pillTextColor: themeColor("textPrimary"),
    pillFontSize: 14,
    pillFontWeight: "medium",
  },
  "bordered-icon": {
    showIcon: true,
    containerGap: 6,
    containerBgColor: themeColor("transparent"),
    containerPaddingX: 0,
    containerPaddingY: 0,
    containerBorderRadius: 0,
    pillPaddingX: 16,
    pillPaddingY: 14,
    pillBorderRadius: 10,
    pillBorderColor: themeColor("borderColor"),
    pillBorderWidth: 1.5,
    pillBgColor: themeColor("secondary"),
    pillBgColorHover: themeColor("background"),
    pillTextColor: themeColor("textPrimary"),
    pillFontSize: 14,
    pillFontWeight: "medium",
  },
  "bordered-highlighted": {
    showIcon: true,
    containerGap: 6,
    containerBgColor: themeColor("transparent"),
    containerPaddingX: 12,
    containerPaddingY: 12,
    containerBorderRadius: 12,
    pillPaddingX: 16,
    pillPaddingY: 14,
    pillBorderRadius: 10,
    pillBorderColor: themeColor("borderColor"),
    pillBorderWidth: 1.5,
    pillBgColor: themeColor("secondary"),
    pillBgColorHover: themeColor("background"),
    pillTextColor: themeColor("textPrimary"),
    pillFontSize: 14,
    pillFontWeight: "medium",
  },
};

const PRESET_OPTIONS = [
  { label: "Plain", value: "plain" },
  { label: "Plain + Icon", value: "plain-icon" },
  { label: "Highlighted", value: "highlighted" },
  { label: "Highlighted + Icon", value: "highlighted-icon" },
  { label: "Container BG", value: "container-bg" },
  { label: "Container BG + Icon", value: "container-bg-icon" },
  { label: "Bordered", value: "bordered" },
  { label: "Bordered + Icon", value: "bordered-icon" },
  { label: "Bordered Highlighted", value: "bordered-highlighted" },
  { label: "Custom", value: "custom" },
];

const editFields: EditField[] = [
  // Preset selector (top of panel)
  {
    key: "preset",
    label: "Preset",
    type: "options",
    options: PRESET_OPTIONS,
  },

  // Layout direction
  {
    key: "layout",
    label: "Layout",
    type: "options",
    options: [
      { label: "Vertical", value: "vertical" },
      { label: "Horizontal", value: "horizontal" },
    ],
  },

  // Icon toggle
  { key: "showIcon", label: "Show Arrow Icon", type: "toggle" },

  // Container
  {
    key: "containerGap",
    label: "Gap Between Pills",
    type: "number",
    min: 4,
    max: 16,
    step: 2,
  },
  {
    key: "containerPaddingX",
    label: "Container Padding X",
    type: "number",
    min: 0,
    max: 32,
    step: 2,
  },
  {
    key: "containerPaddingY",
    label: "Container Padding Y",
    type: "number",
    min: 0,
    max: 32,
    step: 2,
  },
  {
    key: "containerBorderRadius",
    label: "Container Border Radius",
    type: "number",
    min: 0,
    max: 24,
    step: 2,
  },

  // Pill styling
  {
    key: "pillPaddingX",
    label: "Pill Padding X",
    type: "number",
    min: 8,
    max: 24,
    step: 2,
  },
  {
    key: "pillPaddingY",
    label: "Pill Padding Y",
    type: "number",
    min: 6,
    max: 20,
    step: 2,
  },
  {
    key: "pillBorderRadius",
    label: "Pill Border Radius",
    type: "number",
    min: 0,
    max: 24,
    step: 2,
  },
  {
    key: "pillBorderWidth",
    label: "Pill Border Width",
    type: "number",
    min: 0,
    max: 3,
    step: 0.5,
  },
  { key: "pillBorderColor", label: "Pill Border Color", type: "color" },
  { key: "pillBgColor", label: "Pill Background", type: "color" },
  { key: "pillBgColorHover", label: "Pill Hover Background", type: "color" },
  { key: "pillTextColor", label: "Pill Text Color", type: "color" },
  { key: "pillShadowEnabled", label: "Pill Shadow", type: "toggle" },
  { key: "pillShadow", label: "Shadow Value (CSS)", type: "text" },
  {
    key: "pillFontSize",
    label: "Pill Font Size",
    type: "number",
    min: 12,
    max: 18,
    step: 1,
  },
  {
    key: "pillFontWeight",
    label: "Pill Font Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_KEYWORDS],
  },
  { key: "pillFontFamily", label: "Pill Font Family", type: "font" },

  // Shadow
  { key: "pillShadowEnabled", label: "Pill Shadow", type: "toggle" },
  { key: "pillShadow", label: "Pill Shadow CSS", type: "text" },
];

export const SuggestionListWidget: React.FC<SuggestionListWidgetProps> = ({
  initialState = {},
  widgetId,
  displayName,
  children,
}) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange: rawHandleEditChange,
    handleSave,
    themeColors,
    themeFonts,
    resolveColor,
    canSave,
  } = useWidgetEditable<SuggestionListWidgetState>(
    widgetId,
    { ...defaultState, ...initialState },
    editFields
  );

  // Wrap handleEditChange to apply preset values when preset changes
  const handleEditChange = (key: string, value: any) => {
    if (key === "preset" && value !== "custom") {
      const presetKey = value as Exclude<SuggestionListPreset, "custom">;
      const presetValues = presetDefinitions[presetKey];
      if (presetValues) {
        // Apply the preset key first
        rawHandleEditChange("preset", value);
        // Then apply all preset values
        Object.entries(presetValues).forEach(([k, v]) => {
          rawHandleEditChange(k, v);
        });
        return;
      }
    }
    rawHandleEditChange(key, value);
  };

  // Resolve font family - if "inherit", use inherit; otherwise look up from themeFonts
  const fontKey = state.pillFontFamily;
  const fontData =
    fontKey && fontKey !== "inherit" ? themeFonts[fontKey] : null;

  // For Google Fonts, we need to load via <link> tag in document.head
  // (not inline, because Shadow DOM isolates @font-face declarations)
  const googleFontLink =
    fontData && fontData.format === "google" ? fontData.url : null;

  // Load Google Font into document.head when font changes
  // NOTE: This hook must be called before any early returns to satisfy React's rules of hooks
  useEffect(() => {
    if (googleFontLink) {
      loadGoogleFontInHead(googleFontLink);
    }
  }, [googleFontLink]);

  if (isLoading) {
    return <></>;
  }

  // Determine font-family CSS based on font type
  // - system: use the url field which contains the CSS font-family value
  // - google/upload: use the font name
  const fontFamilyCss = fontData
    ? fontData.format === "system"
      ? fontData.url // System fonts store CSS font-family in url field
      : `"${fontData.name}", sans-serif`
    : "inherit";

  // Determine if we need @font-face (only for uploaded fonts with actual file URLs)
  const needsFontFace =
    fontData && fontData.format !== "system" && fontData.format !== "google";

  // Generate unique class for this widget instance (stable based on widgetId)
  const uniqueClass = `suggestion-list-${widgetId || "default"}`;

  // Generate @font-face CSS only for uploaded fonts
  // Also include !important override rule for font-family
  const styleCss = `
    ${
      needsFontFace
        ? `
      @font-face {
        font-family: "${fontData.name}";
        src: url("${fontData.url}") format("${fontData.format}");
        font-display: swap;
      }
    `
        : ""
    }
    ${
      fontFamilyCss !== "inherit"
        ? `.${uniqueClass} { font-family: ${fontFamilyCss} !important; }`
        : ""
    }
  `.trim();

  // Create state with computed fontFamilyCss for children
  const stateWithFont: SuggestionListWidgetState = {
    ...state,
    pillFontFamilyCss: fontFamilyCss,
  };

  // Resolve container background
  const containerBg = resolveColor(state.containerBgColor);
  const hasContainerBg =
    containerBg && containerBg !== "transparent" && containerBg !== "";

  const widgetContent = (
    <>
      {styleCss && <style>{styleCss}</style>}
      <div
        className={uniqueClass}
        style={{
          backgroundColor: hasContainerBg ? containerBg : undefined,
          padding:
            hasContainerBg || state.containerPaddingX || state.containerPaddingY
              ? `${state.containerPaddingY}px ${state.containerPaddingX}px`
              : undefined,
          borderRadius:
            state.containerBorderRadius > 0
              ? `${state.containerBorderRadius}px`
              : undefined,
        }}
      >
        {children(stateWithFont, resolveColor)}
      </div>
    </>
  );

  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
      themeFonts={themeFonts}
    >
      {widgetContent}
    </WidgetWrapper>
  );
};
